import React from 'react';
import { Flipper, Flipped } from 'react-flip-toolkit';
import './styles.css';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import {
  PendingSupportRequestListItem,
  SquarePill,
} from './Flipper.SupportRequestPending.ListItem';
import { ScrollView, View } from '@droplet-tech-code/core-elements/module/ui/View';
import { translate } from '~/utils/translation.utils';
import { PageHeader } from '~/components/Page/Page.Header';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { useSupportRequestStore } from '../../utils/supportRequest.store';
import { BaseInput } from '@droplet-tech-code/core-elements/module/ui/Inputs/BaseInputs';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';

export const SupportRequestsList = () => {
  const flipKey = useSupportRequestStore(
    (state) =>
      state.activeListIdsSearched.join() +
      state.inactiveListIdsSearched.join() +
      Object.entries(state.activeMap).flat().join() +
      Object.entries(state.inactiveMap).flat().join(),
  );
  return (
    <VStack flex={1} bg="white">
      <SearchBar />
      <Toggles />
      <VStack flex={1}>
        <Flipper flipKey={flipKey} spring="gentle" className="flipper-flex">
          <InactiveList />
          <Divider vertical />
          <ActiveList />
        </Flipper>
      </VStack>
    </VStack>
  );
};

const Toggles = () => {
  const focus = useSupportRequestStore((state) => state.focus)['unassignedBookings'];
  const setFocus = useSupportRequestStore((state) => state.setFocus);
  return (
    <HStack p="2.5">
      <SquarePill
        text="Without Booking"
        bgColor={focus ? '#5D5' : '#CCC'}
        fColor={focus ? '#050' : '#111'}
        onPress={() => setFocus({ unassignedBookings: !focus })}
      />
    </HStack>
  );
};

const ActiveList = () => {
  const list = useSupportRequestStore(
    (state) => state.activeListIdsSearched,
    (a, b) => a.join('-') === b.join('-'),
  );
  return (
    <InternalList
      list={list}
      title={translate('supportRequests.activeSupportRequests')}
      isActive
    />
  );
};

const InactiveList = () => {
  const list = useSupportRequestStore(
    (state) => state.inactiveListIdsSearched,
    (a, b) => a.join('-') === b.join('-'),
  );
  return (
    <InternalList
      list={list}
      title={translate('supportRequests.inactiveSupportRequests')}
      isActive={false}
    />
  );
};

const SearchBar = () => {
  const searchTerm = useSupportRequestStore((state) => state.searchTerm);
  const setSearchTerm = useSupportRequestStore((state) => state.setSearchTerm);

  return (
    <BaseInput
      placeholder={translate('routeSearch.search')}
      iconStart="search"
      inputContainerStyle={[
        {
          backgroundColor: 'white',
          borderRadius: 0,
          borderWidth: 0,
        },
      ]}
      value={searchTerm}
      onChangeText={(text) => {
        setSearchTerm(text);
      }}
    />
  );
};

const InternalList = ({
  list,
  title,
  isActive,
}: {
  list: string[];
  title: string;
  isActive: boolean;
}) => {
  return (
    <VStack style={{ width: 300 }}>
      <ListHeader title={title} list={list} isActive={isActive} />
      <ScrollView>
        <VStack flex={1} style={{ alignItems: 'center' }} py="2.5" space="2.5">
          {list.map((groupId) => (
            <Flipped flipId={groupId} key={groupId}>
              <div>
                <View>
                  <PendingSupportRequestListItem groupId={groupId} isActive={isActive} />
                </View>
              </div>
            </Flipped>
          ))}
        </VStack>
      </ScrollView>
    </VStack>
  );
};
const PendingCount = ({
  custCount,
  requestCount,
}: {
  custCount: number;
  requestCount: number;
}) => {
  return (
    <VStack>
      <Text.Small color="monochrome-mid">{`${custCount}  ${translate(
        'supportRequests.customers',
      )}`}</Text.Small>
      <Text.Small color="monochrome-mid">{`${requestCount}  ${translate(
        'supportRequests.requests',
      )}`}</Text.Small>
    </VStack>
  );
};

const ListHeader = ({
  title,
  isActive,
  list,
}: {
  list: string[];
  title: string;
  isActive: boolean;
}) => {
  const requestsMap = useSupportRequestStore((state) => {
    if (isActive) {
      return state.activeMap;
    }
    return state.inactiveMap;
  });

  const validEntries = Object.entries(requestsMap).filter(([key, _val]) =>
    list.includes(key),
  );
  const requestIds = validEntries.map(([_key, val]) => val);
  const requestCount = requestIds.flat().length;
  return (
    <PageHeader
      title={title}
      Subtitle={PendingCount({
        custCount: list?.length || 0,
        requestCount,
      })}
    />
  );
};
